// Atlas Dashboard — Tracked Emails Page
// Lists all tracked emails with filtering and status badges.

import { useState } from "react";
import { Mail, Eye, MousePointer, MessageCircle, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { useTrackedEmails } from "../hooks/useApi";
import { formatIst, formatIstDate, truncate } from "../lib/format";
import { OPEN_STATUS_CONFIG, REPLY_STATUS_CONFIG } from "@atlas/shared";
import type { TrackedEmail, OpenStatusLabel, ReplyStatus } from "@atlas/shared";

export function EmailsPage() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<string>("all");
  const { data, loading, error } = useTrackedEmails(page, filter !== "all" ? { openStatus: filter } : undefined);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data || data.items.length === 0) return <EmptyState />;

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px", alignItems: "center" }}>
        <Filter size={16} color="var(--color-text-tertiary)" />
        <FilterButton label="All" active={filter === "all"} onClick={() => { setFilter("all"); setPage(1); }} />
        <FilterButton label="Opened" active={filter === "opened"} onClick={() => { setFilter("opened"); setPage(1); }} />
        <FilterButton label="Likely opened" active={filter === "likely_opened"} onClick={() => { setFilter("likely_opened"); setPage(1); }} />
        <FilterButton label="Not detected" active={filter === "not_detected"} onClick={() => { setFilter("not_detected"); setPage(1); }} />
      </div>

      {/* Email table */}
      <div style={{ backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <thead>
            <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "1px solid var(--color-border)" }}>
              <Th>Subject</Th>
              <Th>Recipient</Th>
              <Th>Sent</Th>
              <Th>Opened</Th>
              <Th>Clicked</Th>
              <Th>Reply</Th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((email: TrackedEmail) => (
              <EmailRow key={email.trackingId} email={email} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
        <span style={{ fontSize: "13px", color: "var(--color-text-tertiary)" }}>
          {data.total} emails • Page {data.page}
        </span>
        <div style={{ display: "flex", gap: "8px" }}>
          <PageButton disabled={page === 1} onClick={() => setPage(page - 1)}>
            <ChevronLeft size={16} /> Previous
          </PageButton>
          <PageButton disabled={!data.hasMore} onClick={() => setPage(page + 1)}>
            Next <ChevronRight size={16} />
          </PageButton>
        </div>
      </div>
    </div>
  );
}

function EmailRow({ email }: { email: TrackedEmail }) {
  const openConfig = OPEN_STATUS_CONFIG[email.openStatus as OpenStatusLabel];
  const replyConfig = REPLY_STATUS_CONFIG[email.replyStatus as ReplyStatus];

  return (
    <tr
      style={{ borderBottom: "1px solid var(--color-border)", cursor: "pointer", transition: "background 0.15s" }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      <Td>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {email.isPriority && <span style={{ color: "#D97706", fontSize: "12px" }}>★</span>}
          {truncate(email.subject, 50)}
        </div>
      </Td>
      <Td>{truncate(email.recipientEmail, 30)}</Td>
      <Td style={{ color: "var(--color-text-tertiary)", fontSize: "13px" }}>{formatIstDate(email.sentAt)}</Td>
      <Td>
        <Badge color={openConfig?.color || "gray"} label={email.openCount > 0 ? `${openConfig?.label} ${email.openCount}x` : openConfig?.label || "—"} />
      </Td>
      <Td>
        <Badge color={email.clickCount > 0 ? "blue" : "gray"} label={email.clickCount > 0 ? `${email.clickCount} clicks` : "—"} />
      </Td>
      <Td>
        <Badge color={replyConfig?.color || "gray"} label={replyConfig?.label || "—"} />
      </Td>
    </tr>
  );
}

// ─── Shared UI components ───

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{ textAlign: "left", padding: "12px 16px", fontSize: "12px", fontWeight: 600, color: "var(--color-text-tertiary)", textTransform: "uppercase" as const, letterSpacing: "0.5px" }}>
      {children}
    </th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: "12px 16px", color: "var(--color-text-primary)", ...style }}>{children}</td>;
}

function Badge({ color, label }: { color: string; label: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    green: { bg: "#F0FDF4", text: "#16A34A" },
    amber: { bg: "#FFFBEB", text: "#D97706" },
    gray: { bg: "#F9FAFB", text: "#6B7280" },
    blue: { bg: "#EFF6FF", text: "#2563EB" },
    red: { bg: "#FEF2F2", text: "#DC2626" },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "4px", backgroundColor: c.bg, color: c.text, fontSize: "12px", fontWeight: 600 }}>
      {label}
    </span>
  );
}

function FilterButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 12px",
        borderRadius: "6px",
        border: "1px solid active ? '#2563EB' : 'var(--color-border)'",
        backgroundColor: active ? "#EFF6FF" : "transparent",
        color: active ? "#2563EB" : "var(--color-text-secondary)",
        fontSize: "13px",
        fontWeight: active ? 600 : 400,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function PageButton({ children, disabled, onClick }: { children: React.ReactNode; disabled: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        padding: "6px 12px",
        borderRadius: "6px",
        border: "1px solid var(--color-border)",
        backgroundColor: "var(--color-card)",
        color: disabled ? "var(--color-text-tertiary)" : "var(--color-text-primary)",
        fontSize: "13px",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function LoadingState() {
  return (
    <div style={{ padding: "48px", textAlign: "center", color: "var(--color-text-tertiary)" }}>
      Loading your tracked emails...
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div style={{ padding: "48px", textAlign: "center", color: "#DC2626" }}>
      {message}
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ padding: "48px", textAlign: "center" }}>
      <Mail size={48} style={{ color: "var(--color-text-tertiary)", margin: "0 auto 16px" }} />
      <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}>No tracked emails yet</h3>
      <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>
        Install the Chrome extension or compose an email from the dashboard to start tracking.
      </p>
    </div>
  );
}
